import { cn } from "cn";
import { Check, ChevronRight, X } from "lucide-react";
import type * as React from "react";
import { Meter } from "./meter";

// A short "first steps" checklist: a title with "2 of 4", the ink progress
// meter, then one row per step. A done step shows a filled ink check and
// its label in the quiet text colour; an open step shows an empty ring and,
// when it has one, its action (the whole row is the button, 44px on
// phones). Optional steps say so. A close button dismisses the card.

export interface ChecklistItem {
  id: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  done: boolean;
  /** Shown as "Optional" and not counted in the progress. */
  optional?: boolean;
  /** Pressing the open step. */
  onSelect?: () => void;
}

export interface ChecklistProps {
  title: React.ReactNode;
  /** "2 of 4". */
  progressLabel: string;
  /** The meter's accessible name. */
  meterLabel: string;
  items: readonly ChecklistItem[];
  optionalLabel: string;
  dismissLabel: string;
  onDismiss?: () => void;
  /** Under the steps (e.g. a beta feedback button). */
  footer?: React.ReactNode;
  className?: string;
  testId?: string;
}

/** Done and total, without the optional steps. */
export const checklistProgress = (
  items: readonly Pick<ChecklistItem, "done" | "optional">[],
): { done: number; total: number } => {
  const required = items.filter((item) => !item.optional);
  return {
    done: required.filter((item) => item.done).length,
    total: required.length,
  };
};

export function Checklist({
  title,
  progressLabel,
  meterLabel,
  items,
  optionalLabel,
  dismissLabel,
  onDismiss,
  footer,
  className,
  testId,
}: ChecklistProps): React.JSX.Element {
  const { done, total } = checklistProgress(items);
  return (
    <section
      className={cn(
        "rounded-v3-panel bg-v3-panel p-4 font-v3 text-v3-text shadow-v3-hairline",
        className,
      )}
      data-slot="checklist"
      data-testid={testId}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-semibold leading-5 tracking-[-0.01em]">
            {title}
          </h2>
          <p className="mt-0.5 text-[12.5px] tabular-nums text-v3-text-3">
            {progressLabel}
          </p>
        </div>
        {onDismiss && (
          <button
            aria-label={dismissLabel}
            className="-mr-2 -mt-2 inline-flex size-11 shrink-0 items-center justify-center rounded-full text-v3-text-3 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-2 focus-visible:outline-v3-brand lg:size-8"
            data-testid={testId ? `${testId}-dismiss` : undefined}
            title={dismissLabel}
            type="button"
            onClick={onDismiss}
          >
            <X aria-hidden className="size-4" />
          </button>
        )}
      </div>
      <Meter
        className="mt-3 h-1.5"
        label={meterLabel}
        tone="ink"
        value={total ? done / total : 0}
      />
      <ul className="mt-2 -mx-2">
        {items.map((item) => {
          const content = (
            <>
              <span
                aria-hidden
                className={cn(
                  "mt-px flex size-5 shrink-0 items-center justify-center rounded-full",
                  item.done
                    ? "bg-v3-ink text-v3-on-ink"
                    : "shadow-[inset_0_0_0_1.5px_var(--v3-border-strong)]",
                )}
              >
                {item.done && <Check className="size-3" strokeWidth={3} />}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "block text-[14px] font-medium leading-5",
                    item.done && "text-v3-text-3",
                  )}
                >
                  {item.label}
                  {item.optional && (
                    <span className="ml-1.5 text-[12px] font-normal text-v3-text-3">
                      {optionalLabel}
                    </span>
                  )}
                </span>
                {item.description && !item.done && (
                  <span className="mt-0.5 block text-[12.5px] leading-snug text-v3-text-3">
                    {item.description}
                  </span>
                )}
              </span>
              {item.onSelect && !item.done && (
                <ChevronRight
                  aria-hidden
                  className="mt-0.5 size-4 shrink-0 text-v3-text-3"
                />
              )}
            </>
          );
          const row =
            "flex w-full items-start gap-3 rounded-[10px] px-2 py-2.5 text-left";
          return (
            <li
              key={item.id}
              data-done={item.done || undefined}
              data-testid={testId ? `${testId}-${item.id}` : undefined}
            >
              {item.onSelect && !item.done ? (
                <button
                  className={cn(
                    row,
                    "min-h-11 transition-colors hover:bg-v3-surface-2 focus-visible:outline-2 focus-visible:outline-v3-brand",
                  )}
                  type="button"
                  onClick={item.onSelect}
                >
                  {content}
                </button>
              ) : (
                <div className={row}>{content}</div>
              )}
            </li>
          );
        })}
      </ul>
      {footer && (
        <div className="-mx-2 -mb-2 mt-1 border-t border-v3-border pt-1">
          {footer}
        </div>
      )}
    </section>
  );
}
